import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import IndicationCriteriaCard from './IndicationCriteriaCard';
import './CoverageCriteriaTab.css';

/**
 * CoverageCriteriaTab - Main tab component for Coverage Criteria by Indication
 * @param {Object} props
 * @param {Object} props.coverageData - Full coverage data including indications
 * @param {number} props.productId - Product ID
 * @param {number} props.formularyId - Formulary ID
 * @param {string} props.productName - Product name
 * @param {string} props.planName - Plan name
 * @param {Object} props.indicationCriteriaCache - Cached indication criteria data
 * @param {Function} props.onIndicationCriteriaUpdate - Callback to update cache
 */
const CoverageCriteriaTab = ({
  coverageData,
  productId,
  formularyId,
  productName,
  planName,
  indicationCriteriaCache,
  onIndicationCriteriaUpdate
}) => {
  // Extract indications from coverage data
  const indications = coverageData?.indications || [];
  
  // Normalize indication data to match component expectations
  const normalizedIndications = indications.map(ind => ({
    IndicationId: ind.IndicationId,
    name: ind.Name, // Convert capital N from API to lowercase for component
  }));
  
  // Determine overall coverage status from the actual status field
  const coverageStatus = coverageData?.coverage?.status || coverageData?.status || 'no_data';

  // Debug logging
  console.log('CoverageCriteriaTab - Coverage Data:', {
    isCovered: coverageData?.isCovered,
    hasData: coverageData?.hasData,
    status: coverageData?.coverage?.status || coverageData?.status,
    determinedCoverageStatus: coverageStatus,
    coverageDataKeys: coverageData ? Object.keys(coverageData) : []
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Coverage Criteria by Indication</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Detailed coverage criteria, prior authorization, and step therapy requirements by indication
        </p>
      </div>

      {normalizedIndications.length === 0 ? (
        // Empty state - simple message without extra Card wrapper
        <div className="p-12 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
            <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0-4h.01" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">
              Indication Criteria Not Provided
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              The plan has not specified coverage criteria for specific indications. Standard criteria may apply—review the Notes tab for details.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Indication Cards */}
          {normalizedIndications.map((indication) => (
            <IndicationCriteriaCard
              key={indication.IndicationId}
              indication={indication}
              productId={productId}
              formularyId={formularyId}
              productName={productName}
              planName={planName}
              coverageStatus={coverageStatus}
              cachedData={indicationCriteriaCache?.[indication.IndicationId]}
              onCacheUpdate={(data) => onIndicationCriteriaUpdate(indication.IndicationId, data)}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default CoverageCriteriaTab;
